import { useState, useEffect, useCallback } from 'react';
import {
  api,
  CreditBalance,
  CreditTransaction,
  CreditTransactionsResult,
  SpendingCategorySummary,
} from '../../lib/api';

interface UseCreditsReturn {
  balance: CreditBalance | null;
  spendingSummary: SpendingCategorySummary[];
  transactions: CreditTransaction[];
  transactionsTotal: number;
  loading: boolean;
  loadingTransactions: boolean;
  error: string | null;
  refresh: () => void;
  loadMoreTransactions: () => void;
  hasMoreTransactions: boolean;
}

export function useCredits(): UseCreditsReturn {
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [spendingSummary, setSpendingSummary] = useState<SpendingCategorySummary[]>([]);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const limit = 20;

  const fetchBalance = useCallback(async () => {
    try {
      const result = await api.getCreditBalance();
      setBalance(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load credit balance');
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const result = await api.getCreditSpendingSummary();
      setSpendingSummary(result.categories);
    } catch {
      // Non-critical — summary failure shouldn't block the page
    }
  }, []);

  const fetchTransactions = useCallback(async (newOffset: number = 0, append: boolean = false) => {
    setLoadingTransactions(true);
    try {
      const result: CreditTransactionsResult = await api.getCreditTransactions({
        limit,
        offset: newOffset,
      });
      if (append) {
        setTransactions((prev) => [...prev, ...result.transactions]);
      } else {
        setTransactions(result.transactions);
      }
      setTransactionsTotal(result.total);
      setOffset(newOffset);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load transactions');
    } finally {
      setLoadingTransactions(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    await Promise.all([fetchBalance(), fetchSummary(), fetchTransactions(0, false)]);
    setLoading(false);
  }, [fetchBalance, fetchSummary, fetchTransactions]);

  const loadMoreTransactions = useCallback(() => {
    if (!loadingTransactions && offset + limit < transactionsTotal) {
      fetchTransactions(offset + limit, true);
    }
  }, [fetchTransactions, loadingTransactions, offset, transactionsTotal]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    balance,
    spendingSummary,
    transactions,
    transactionsTotal,
    loading,
    loadingTransactions,
    error,
    refresh,
    loadMoreTransactions,
    hasMoreTransactions: offset + limit < transactionsTotal,
  };
}
